import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      api.setAuthToken(storedToken);
    }
    setLoading(false);
  }, []);

  // Listen for global auth-expired events (fired by api.js on 401) so we
  // clear in-memory state and re-render as guest instead of getting stuck
  // in a "logged-in but every request 401s" limbo.
  useEffect(() => {
    const handler = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.login(email, password);
    
    setToken(response.access_token);
    setUser(response.user);
    
    localStorage.setItem('auth_token', response.access_token);
    localStorage.setItem('auth_user', JSON.stringify(response.user));
    api.setAuthToken(response.access_token);
    
    return response.user;
  }, []);

  const register = useCallback(async (firstName, lastName, email, password) => {
    // Grace-window flow: server returns JWT immediately (email_verified=false).
    // User can browse + shop for 24h. Checkout endpoints (create-session,
    // paypal/create-order) reject unverified users with 403 EMAIL_NOT_VERIFIED,
    // and the frontend shows a persistent "Verify your email" banner.
    const response = await api.register(firstName, lastName, email, password);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem('auth_token', response.access_token);
    localStorage.setItem('auth_user', JSON.stringify(response.user));
    api.setAuthToken(response.access_token);
    return response.user;
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    const response = await api.verifyOtp(email, otp);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem('auth_token', response.access_token);
    localStorage.setItem('auth_user', JSON.stringify(response.user));
    api.setAuthToken(response.access_token);
    return response.user;
  }, []);

  const resendOtp = useCallback(async (email) => {
    return await api.resendOtp(email);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    api.setAuthToken(null);
  }, []);

  const updateUser = useCallback(async (data) => {
    const updatedUser = await api.updateProfile(data);
    setUser(updatedUser);
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    return updatedUser;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      // Token might be expired
      logout();
      return null;
    }
  }, [token, logout]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    register,
    verifyOtp,
    resendOtp,
    logout,
    updateUser,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
